import React, { useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SiteProvider, useSite } from './contexts/SiteContext';
import { CartProvider } from './contexts/CartContext';

// Page imports
import HomePage from './pages/HomePage';
import LocationsPage from './pages/LocationsPage';
import MembershipPage from './pages/MembershipPage';
import PrepaidSuccess from './pages/PrepaidSuccess';
import EposReceipt from './pages/EposReceipt';
import CartPage from './pages/CartPage';
import BookTourPage from './pages/BookTourPage';
import LoginPage from './pages/LoginPage';
import ContactPage from './pages/ContactPage';
import DayPassPage from './pages/DayPassPage';
import DayPassSuccess from './pages/DayPassSuccess';
import DayPassView from './pages/DayPassView';

// Member imports
import MemberHubPage from './pages/member/MemberHub';
import MemberProfile from './pages/member/MemberProfile';
import MemberArrears from './pages/member/MemberArrears';
import MemberArrearsSuccess from './pages/member/MemberArrearsSuccess';
import FitnessTracker from './pages/member/FitnessTracker';

// PT imports
import PTRegister from './pages/pt/PTRegister';
import PTLogin from './pages/pt/PTLogin';
import PTHub from './pages/pt/PTHub';

// Admin imports
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminMembers from './pages/admin/AdminMembers';
import AdminBilling from './pages/admin/AdminBilling';
import AdminAccess from './pages/admin/AdminAccess';
import AdminOrders from './pages/admin/AdminOrders';
import AdminSites from './pages/admin/AdminSites';
import AdminReports from './pages/admin/AdminReports';
import AdminAssets from './pages/admin/AdminAssets';
import AdminAuditLogs from './pages/admin/AdminAuditLogs';
import AdminPTs from './pages/admin/AdminPTs';
import AdminStaffManagement from './pages/admin/AdminStaffManagement';
import AdminFinanceExport from './pages/admin/AdminFinanceExport';
import CRMHub from './pages/admin/CRMHub';
import AdminEmailTemplates from './pages/admin/AdminEmailTemplates';
import AdminReportBuilder from './pages/admin/AdminReportBuilder';
import StaffPortal from './pages/admin/StaffPortal';
import AdminDevices from './pages/admin/AdminDevices';
import AdminBannedList from './pages/admin/AdminBannedList';
import AdminApplicationReview from './pages/admin/AdminApplicationReview';
import AdminPermissions from './pages/admin/AdminPermissions';
import AdminUsers from './pages/admin/AdminUsers';
import AdminEposConfigs from './pages/admin/AdminEposConfigs';
import AdminFinanceDashboard from './pages/admin/AdminFinanceDashboard';
import AdminSarExport from './pages/admin/AdminSarExport';
import AdminSubscriptions from './pages/admin/AdminSubscriptions';
import AdminDayPasses from './pages/admin/AdminDayPasses';
import AdminPlans from './pages/admin/AdminPlans';
import AdminTours from './pages/admin/AdminTours';
import AutomationBuilder from './pages/admin/AutomationBuilder';
import PTDetectionDashboard from './pages/admin/PTDetectionDashboard';
import AdminDDFiles from './pages/admin/AdminDDFiles';
import AdminDDLifecycle from './pages/admin/AdminDDLifecycle';
import AdminStock from './pages/admin/AdminStock';
import AdminProducts from './pages/admin/AdminProducts';
import AdminLedgers from './pages/admin/AdminLedgers';
import AdminSuppliers from './pages/admin/AdminSuppliers';
import AdminPurchaseOrders from './pages/admin/AdminPurchaseOrders';
import PurchaseOrderPrint from './pages/admin/PurchaseOrderPrint';
import AdminInventoryReports from './pages/admin/AdminInventoryReports';
import AdminPayroll from './pages/admin/AdminPayroll';
import AdminRevenue from './pages/admin/AdminRevenue';
import AdminGroupOverview from './pages/admin/AdminGroupOverview';
import EPOSTill from './pages/admin/EPOSTill';
import AdminEposBackOffice from './pages/admin/AdminEposBackOffice';
import KnowledgeBase from './pages/admin/KnowledgeBase';

// E-commerce imports
import NewShopPage, { CartProvider as NewCartProvider, CheckoutPage } from './pages/ShopPage';
import { ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from './pages/auth/PasswordReset';

// Shared component imports
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const ADMIN_ROLES = ['super_admin', 'developer_admin', 'area_manager', 'site_manager', 'finance_admin'];

// Permissions that grant access to the admin portal — having ANY of these
// means the user can log in to /admin/*. Per-page button visibility is then
// gated by individual <PermissionGate> components inside each page.
const ADMIN_GATE_PERMS = [
  'members.view', 'members.edit', 'members.create',
  'billing.view', 'shop.view', 'shop.manage', 'shop.fulfil',
  'access.view', 'banned.view', 'leadgen.view', 'reports.view',
  'staff.view', 'staff.create', 'settings.plans', 'settings.sites',
  'purchasing.suppliers', 'purchasing.create_po', 'stock.price_lists',
];

// Protected Route
const ProtectedRoute = ({ children, requireStaff = false, requireAdmin = false }) => {
  const { isAuthenticated, loading: authLoading, isStaff, user } = useAuth();
  const { permissions, roleEnum, loading: siteLoading, hasResolvedOnce } = useSite();
  const location = useLocation();

  // Don't redirect anywhere while auth is still resolving — OR while we
  // haven't yet got the user's permissions even ONCE. This prevents an
  // in-flight refresh() during SPA route navigation from bouncing the user
  // back to /staff-portal mid-flight.
  if (authLoading || (requireAdmin && !hasResolvedOnce && siteLoading)) {
    return (
      <div className="min-h-screen bg-uf-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-uf-red"></div>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  if (requireStaff && !isStaff()) return <Navigate to="/" replace />;

  // Admin portal access — accept either a legacy admin role enum OR any
  // RBAC permission that maps to an admin feature. This lets receptionists /
  // site staff with role assignments (e.g. "Stock Manager") into /admin.
  if (requireAdmin && isStaff() && hasResolvedOnce) {
    const role = roleEnum || user?.role;
    const hasAdminRole = ADMIN_ROLES.includes(role);
    const hasAdminPerm = (permissions || []).some(p => ADMIN_GATE_PERMS.includes(p));
    if (!hasAdminRole && !hasAdminPerm) {
      return <Navigate to="/staff-portal" replace />;
    }
  }
  return children;
};

// Public layout with Navbar + Footer
const Layout = ({ children }) => (
  <>
    <div className="noise-overlay"></div>
    <Navbar />
    <main>{children}</main>
    <Footer />
  </>
);

function App() {
  return (
    <AuthProvider>
      <SiteProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Admin Routes */}
            <Route path="/admin/login" element={<Navigate to="/login" replace />} />
            <Route path="/admin" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/members" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminMembers /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/billing" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminBilling /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/subscriptions" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminSubscriptions /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/day-passes" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminDayPasses /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/access" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminAccess /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/shop" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminOrders /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/sites" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminSites /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/reports" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminReports /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/assets" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminAssets /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/audit-logs" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminAuditLogs /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/personal-trainers" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminPTs /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/staff" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminStaffManagement /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/finance" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminFinanceExport /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/crm" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><CRMHub /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/email-templates" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminEmailTemplates /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/report-builder" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminReportBuilder /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/devices" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminDevices /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/banned" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminBannedList /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/applications" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminApplicationReview /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/permissions" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminPermissions /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminUsers /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/epos-configs" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminEposConfigs /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/finance-dashboard" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminFinanceDashboard /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/sar-export" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminSarExport /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/plans" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminPlans /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/tours" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminTours /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/pt-detection" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><PTDetectionDashboard /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/dd-files" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminDDFiles /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/dd-lifecycle" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminDDLifecycle /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/stock" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminStock /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/products" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminProducts /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/ledgers" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminLedgers /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/suppliers" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminSuppliers /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/purchase-orders" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminPurchaseOrders /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/purchase-orders/:poId/print" element={<ProtectedRoute requireStaff><PurchaseOrderPrint /></ProtectedRoute>} />
            <Route path="/admin/inventory-reports" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminInventoryReports /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/payroll" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminPayroll /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/revenue" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminRevenue /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/group-overview" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminGroupOverview /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/epos-backoffice" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminEposBackOffice /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/knowledge-base" element={<ProtectedRoute requireStaff><AdminLayout><KnowledgeBase /></AdminLayout></ProtectedRoute>} />
            <Route path="/epos" element={<EPOSTill />} />
            <Route path="/epos/receipt/:txnId" element={<EposReceipt />} />
            <Route path="/admin/automations/:journeyId" element={<ProtectedRoute requireStaff><AutomationBuilder /></ProtectedRoute>} />
            <Route path="/staff-portal" element={<ProtectedRoute requireStaff><StaffPortal /></ProtectedRoute>} />
            <Route path="/admin/*" element={<ProtectedRoute requireStaff requireAdmin><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />

            {/* Member Hub Routes */}
            <Route path="/hub" element={<ProtectedRoute><MemberHubPage /></ProtectedRoute>} />
            <Route path="/hub/fitness" element={<ProtectedRoute><FitnessTracker /></ProtectedRoute>} />
            <Route path="/hub/profile" element={<ProtectedRoute><MemberProfile /></ProtectedRoute>} />
            <Route path="/member/arrears" element={<ProtectedRoute><MemberArrears /></ProtectedRoute>} />
            <Route path="/member/arrears/success" element={<ProtectedRoute><MemberArrearsSuccess /></ProtectedRoute>} />

            {/* PT Routes */}
            <Route path="/pt/register" element={<PTRegister />} />
            <Route path="/pt/login" element={<PTLogin />} />
            <Route path="/pt/hub" element={<PTHub />} />

            {/* Auth Routes */}
            <Route path="/forgot-password" element={<Layout><ForgotPasswordPage /></Layout>} />
            <Route path="/reset-password" element={<Layout><ResetPasswordPage /></Layout>} />
            <Route path="/verify-email" element={<Layout><VerifyEmailPage /></Layout>} />

            {/* E-commerce Routes */}
            <Route path="/shop" element={<NewCartProvider><NewShopPage /></NewCartProvider>} />
            <Route path="/checkout" element={<NewCartProvider><CheckoutPage /></NewCartProvider>} />
            <Route path="/shop/order-success" element={<NewCartProvider><CheckoutPage /></NewCartProvider>} />

            {/* Public Routes */}
            <Route path="/" element={<Layout><HomePage /></Layout>} />
            <Route path="/locations" element={<Layout><LocationsPage /></Layout>} />
            <Route path="/membership" element={<Layout><MembershipPage /></Layout>} />
            <Route path="/membership/prepaid-success" element={<Layout><PrepaidSuccess /></Layout>} />
            <Route path="/cart" element={<Layout><CartPage /></Layout>} />
            <Route path="/book-tour" element={<Layout><BookTourPage /></Layout>} />
            <Route path="/login" element={<Layout><LoginPage /></Layout>} />
            <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
            <Route path="/day-pass" element={<DayPassPage />} />
            <Route path="/day-pass/success" element={<DayPassSuccess />} />
            <Route path="/day-pass/view/:id" element={<DayPassView />} />
          </Routes>
        </BrowserRouter>
      </CartProvider>
      </SiteProvider>
    </AuthProvider>
  );
}

export default App;
